/**
 * Web component for Anear navigation actions
 *
 * Usage:
 *   <anear-nav action="back">Go Back</anear-nav>
 *   <anear-nav action="join">Join Event</anear-nav>
 *   <anear-nav action="cancel">Cancel</anear-nav>
 *   <anear-nav action="exit" prompt="Close room for everyone?" stay="No, Keep Open" exit="Yes, Close">Close Room</anear-nav>
 *
 * The component will be initialized by useAppDisplayDomSetup with:
 * - action: The navigation action (e.g., "back", "join", "cancel")
 * - onNavClick: Callback function to handle navigation clicks
 */
class AnearNav extends HTMLElement {
  constructor() {
    super()
    this._action = null
    this._onNavClick = null
    this._boundClickHandler = null
  }

  static get observedAttributes() {
    return ['action']
  }

  connectedCallback() {
    // Don't override display or cursor - let CSS classes handle everything
    this.updateClickHandler()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'action') {
      this._action = newValue
      this.updateClickHandler()
    }
  }

  /**
   * Initialize the component with navigation handler
   * Called by useAppDisplayDomSetup
   */
  initialize(onNavClick) {
    this._onNavClick = onNavClick
    this.updateClickHandler()
  }

  get action() {
    return this.getAttribute('action') || this._action
  }

  set action(value) {
    if (value) {
      this.setAttribute('action', value)
      this._action = value
    } else {
      this.removeAttribute('action')
      this._action = null
    }
    this.updateClickHandler()
  }

  updateClickHandler() {
    const action = this.action
    const shouldHandleClick = action && action !== 'null' && this._onNavClick

    if (shouldHandleClick) {
      // Handle click setup entirely within the web component class
      // Remove any existing listener first
      if (this._boundClickHandler) {
        this.removeEventListener('click', this._boundClickHandler, false)
      }

      this._boundClickHandler = (e) => {
        // The handler will find this element via e.target.closest('anear-nav')
        // and get the action from the 'action' attribute
        if (this._onNavClick) {
          this._onNavClick(e)
        }
      }

      // Use bubbling phase (default) - this works naturally with child elements
      this.addEventListener('click', this._boundClickHandler)
    } else {
      // Remove click listener if it exists
      if (this._boundClickHandler) {
        this.removeEventListener('click', this._boundClickHandler, false)
        this._boundClickHandler = null
      }
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this._boundClickHandler) {
      this.removeEventListener('click', this._boundClickHandler, false)
      this._boundClickHandler = null
    }
  }
}

// Register the custom element
if (!customElements.get('anear-nav')) {
  customElements.define('anear-nav', AnearNav)
}

export default AnearNav
